import { useCallback, useEffect, useRef } from 'react';
import { createConsumer, type Subscription } from '@rails/actioncable';
import { captureException } from '@sentry/react';
import type { VideoRecommendationCard } from '@/data/agent/video-recommendation';
import { getAuthToken } from '@/utils/auth-token';

export enum VideoRecommendationEvent {
  TextChunk = 'text_chunk',
  Recommendation = 'recommendation',
  StreamEnded = 'stream_ended',
  Error = 'error',
}

interface ReceivedData extends Record<string, unknown> {
  event: VideoRecommendationEvent;
}

interface UseVideoRecommendationChannelOptions {
  onRecommendation: (video: VideoRecommendationCard) => void;
  onTextMessage: (message: string) => void;
  onError?: (message: string) => void;
  onConnected?: () => void;
}

// Listen-only channel: it subscribes to a per-session stream and dispatches the broadcaster's
// events. The recommendation is triggered by the REST endpoint, so it never performs an action.
export const useVideoRecommendationChannel = ({
  onRecommendation,
  onTextMessage,
  onError,
  onConnected,
}: UseVideoRecommendationChannelOptions) => {
  const consumerRef = useRef<ReturnType<typeof createConsumer> | null>(null);
  const subscriptionRef = useRef<Subscription | null>(null);

  const onRecommendationRef = useRef(onRecommendation);
  const onTextMessageRef = useRef(onTextMessage);
  const onErrorRef = useRef(onError);
  const onConnectedRef = useRef(onConnected);

  onRecommendationRef.current = onRecommendation;
  onTextMessageRef.current = onTextMessage;
  onErrorRef.current = onError;
  onConnectedRef.current = onConnected;

  const disconnect = useCallback(() => {
    subscriptionRef.current?.unsubscribe();
    subscriptionRef.current = null;
    consumerRef.current?.disconnect();
    consumerRef.current = null;
  }, []);

  useEffect(() => () => disconnect(), [disconnect]);

  const subscribe = useCallback(
    (sessionId: string) => {
      disconnect();

      const token = getAuthToken();
      consumerRef.current = createConsumer(`/cable?token=${token}`);
      subscriptionRef.current = consumerRef.current.subscriptions.create(
        {
          channel: 'Agent::Channel::VideoRecommendation',
          session_id: sessionId,
        },
        {
          connected() {
            onConnectedRef.current?.();
          },
          received(data: ReceivedData) {
            const handlers: Record<VideoRecommendationEvent, () => void> = {
              text_chunk: () => {
                if (typeof data.content === 'string') {
                  onTextMessageRef.current(data.content);
                }
              },
              recommendation: () => {
                onRecommendationRef.current(
                  data.video as VideoRecommendationCard
                );
              },
              stream_ended: () => disconnect(),
              error: () => {
                const message =
                  typeof data.message === 'string'
                    ? data.message
                    : 'Unknown error';
                onErrorRef.current?.(message);
                disconnect();
                captureException(
                  new Error(`Video recommendation streaming error: ${message}`)
                );
              },
            };

            handlers[data.event]?.();
          },
          rejected() {
            onErrorRef.current?.('Connection rejected');
          },
        }
      );
    },
    [disconnect]
  );

  return { subscribe, disconnect };
};
